import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isFirstValid } from "@/Common/helpers/Matrices/tiers";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { removeListValue } from "@/Common/helpers/Arrays";

// Matrix helpers
import { getMatrixDimensions, getExactMatrixDimensions, getSmallestBonusMatrixDimensions } from "@/Common/helpers/Matrices";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import RankingBonus from "../RankingBonus";

// MUI
import { Grid } from "@mui/material";
import { detachValidate, isValidMapErrors } from "@/Common/hooks/Validations";

const filterErrorsList = (errors, key) => errors.filter((error) => error !== key);

const validateBeforeSend = (tiers = []) => {
	const validation = detachValidate([
		{
			definition: {
				useKey: "nationalRanks",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<=", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "<",
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers,
		},
		{
			definition: {
				useKey: "regionalRanks",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<=", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "<",
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Regional rank invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers?.[0]?.bonuses || [],
		},
		{
			definition: {
				useKey: "bonuses",
				operators: {
					conditions: [
						{
							order: "ROWS",
							type: "STEP",
							on: "value",
							compare: ">=", // If current >= next is good,
						},
						{
							order: "COLUMNS",
							type: "STEP",
							on: "value",
							compare: ">=", // If upper > lower is good,
							deltas: { Dollar: 0, Percent: 0 },
						},
					],
				},
				errorsMap: ["bonus"],
				errorsMapOn: "bonuses",
				message: "Incorrect bonuses definition",
				validationType: "MATRIX",
			},
			section: tiers,
		},
	]);
	return isValidMapErrors(validation);
};

const isAllValid = (makes = []) => {
	const errors = makes.map((make) => validateBeforeSend(make?.tiers || []));
	return !errors.length || [...errors].every((error) => error);
};

const isValidTiersRange = (tiers = []) => {
	if (!tiers?.length) {
		return false;
	}
	const columns = tiers?.[0]?.bonuses?.length || 0;
	return tiers?.length >= 2 || columns >= 2;
};

const isSomeConfiguredModel = (content) => {
	const { makes = [] } = content || {};
	if (!makes?.length) {
		return true;
	}
	return makes?.some(
		(item) => isValidTiersRange(item?.tiers) && isFirstValid(item?.tiers || [], { column: "bonuses", reference: "bonus" })
	);
};

export default function RankingBonusMakes(props) {
	const {
		sectionId = "rankingBonus",
		content = {
			makes: [],
			simultaneousRanking: false,
		},
		franchises = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors, getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	const [isValidModels, setIsValidModels] = useState(true);
	const [model, setModel] = useState(null);
	const isSomeModelConfigured = isSomeConfiguredModel(model);

	const preventSave = useRef([]);
	const matrixDimensions = getMatrixDimensions("bonuses", model?.makes || []);

	const resizeBonuses = (bonuses, newMatrixDimensions, expand = true, shrink = true) => {
		if (expand || shrink) {
			bonuses.forEach((bonus, key) => {
				const matrixDimensionsForBonus = getExactMatrixDimensions("bonuses", bonus?.tiers);

				if (!isSimilarObject(newMatrixDimensions, matrixDimensionsForBonus)) {
					const tiersDifference = newMatrixDimensions.tiers - matrixDimensionsForBonus.tiers;
					const columnsDifference = newMatrixDimensions.columns - matrixDimensionsForBonus.columns;
					if (tiersDifference >= 0 && columnsDifference >= 0) {
						// tiers got bigger
						// columns got bigger
						if (expand) {
							bonus = expandBonus(bonus, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference <= 0 && columnsDifference <= 0) {
						// tiers got smaller
						// columns got smaller
						if (shrink) {
							bonus = shrinkBonus(bonus, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference >= 0 && columnsDifference <= 0) {
						// tiers got bigger
						// columns got smaller
						if (expand) {
							bonus = expandBonus(bonus, newMatrixDimensions.tiers, matrixDimensionsForBonus.columns);
						}
						if (shrink) {
							bonus = shrinkBonus(bonus, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference <= 0 && columnsDifference >= 0) {
						// tiers got smaller
						// columns got bigger
						if (shrink) {
							bonus = shrinkBonus(bonus, newMatrixDimensions.tiers, matrixDimensionsForBonus.columns);
						}
						if (expand) {
							bonus = expandBonus(bonus, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					}
				}
				bonuses[key] = bonus;
			});
		}
		return clone(bonuses);
	};

	const shrinkBonus = (bonus, maxTiers = 1, maxColumns = 1) => {
		// Reducing rows tiers
		bonus.tiers.length = maxTiers;
		bonus.tiers[maxTiers - 1].to.value = 15000;

		for (const tiers of bonus?.tiers || []) {
			// Reducing columns tiers
			tiers.bonuses.length = maxColumns;
			tiers.bonuses[maxColumns - 1].to.value = 15000;
		}
		return clone(bonus);
	};

	const expandBonus = (bonus, maxTiers = 1, maxColumns = 1) => {
		if (bonus?.tiers?.length < maxTiers) {
			preventSave.current.push(getErrorKey(sectionId, bonus?.make));
			bonus.tiers[bonus.tiers.length - 1].to.value = 1;
			while (bonus?.tiers?.length < maxTiers) {
				const newTier = clone(bonus.tiers[bonus.tiers.length - 1]);
				newTier.from.value = 2;
				newTier.bonuses.forEach((tierBonus) => {
					tierBonus.bonus.value = 0;
				});
				bonus?.tiers?.push(newTier);
			}
			bonus.tiers[bonus.tiers.length - 1].to.value = 15000;
		}
		for (const tiers of bonus?.tiers || []) {
			const tierBonuses = tiers?.bonuses;
			if (tierBonuses?.length < maxColumns) {
				preventSave.current.push(getErrorKey(sectionId, bonus?.make));
				tierBonuses[tierBonuses.length - 1].to.value = 1;
				const newTierBonus = clone(tierBonuses[tierBonuses.length - 1]);
				newTierBonus.from.value = 1;
				newTierBonus.bonus.value = 0;
				while (tierBonuses?.length < maxColumns) {
					tierBonuses.push(clone(newTierBonus));
				}
			}
			tierBonuses[tierBonuses.length - 1].to.value = 15000;
		}
		return clone(bonus);
	};

	// Error handler
	const tabsErrors = useMemo(() => {
		let localErrors = [];

		if (errors.size) {
			localErrors = franchises?.map((franchise) => {
				const listErrors = errors?.get(sectionId) || [];
				const error = listErrors?.includes(
					getErrorKey(sectionId, franchise) || preventSave?.current?.includes(getErrorKey(sectionId, franchise))
				);
				return Boolean(error);
			});
		}

		return localErrors;
	}, [sectionId, franchises, errors, getErrorKey]);

	// Actions handlers
	const update = (section, data) => {
		const allValid = isAllValid(data?.makes);
		const isSomeConfigured = isSomeConfiguredModel(data);
		const isValid = allValid && isSomeConfigured;
		const isSimultaneous = Boolean(data?.tabsProps?.simultaneousRanking);

		if (isValid) {
			onActionUpdate?.(section, null, {
				makes: [...data["makes"]],
				simultaneousRanking: isSimultaneous,
			});
		} else {
			setModel(clone(data));
		}
	};

	const handleUpdateSwitches = (section, area, value) => {
		const updatedModel = { ...model };
		updatedModel["tabsProps"][area] = value;
		if (area === "simultaneousRanking" && value) {
			const newDimensionsForAllMatrices = getSmallestBonusMatrixDimensions(updatedModel["makes"]);
			const shrunk = resizeBonuses(
				[...updatedModel["makes"]],
				{
					columns: newDimensionsForAllMatrices?.minColumns,
					tiers: newDimensionsForAllMatrices?.minTiers,
				},
				false,
				true
			);
			updatedModel["makes"] = [...shrunk];
		}
		update(section, updatedModel);
	};

	const handleUpdateTiers = (section, area, data) => {
		const updatedModel = clone(model);

		preventSave.current = removeListValue(preventSave.current, getErrorKey(sectionId, area));
		removeSectionError(section, area);

		const currentMatrixDimensions = getExactMatrixDimensions("bonuses", data);
		matrixDimensions.set(area, currentMatrixDimensions);

		// Search make and update
		updatedModel["makes"]?.forEach((element, key) => {
			if (element.make === area) {
				updatedModel["makes"][key]["tiers"] = data;
			}
		});

		if (model?.tabsProps?.simultaneousRanking) {
			updatedModel["makes"] = resizeBonuses(updatedModel["makes"], currentMatrixDimensions, true, true);
		}

		update(section, updatedModel);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useMemo(() => {
		const sectionErrors = errors?.get(sectionId) || [];
		setIsValidModels(!sectionErrors.length);
	}, [sectionId, errors]);

	useEffect(() => {
		setModel({
			tabsProps: {
				tabs: [...franchises],
				simultaneousRanking: Boolean(content?.simultaneousRanking),
				errors: [],
			},
			makes: clone(content?.makes),
		});

		return () => {
			setModel(null);
			preventSave.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
			setIsValidModels(true);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.makes?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={tabsErrors}
							content={Object.entries(model?.makes)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<RankingBonus
											{...value}
											isSomeConfigured={isSomeModelConfigured}
											readOnly={readOnly}
											onActionUpdate={(make, tiers) => handleUpdateTiers(sectionId, make, tiers)}
											onActionValidation={(make, isValid) => handleValidation(sectionId, make, isValid)}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"newAndPreOwnedRequired"}
									textLeft={true}
									textChecked={"Simultaneous Ranking"}
									textUnChecked={"Simultaneous Ranking"}
									checked={Boolean(model?.tabsProps?.simultaneousRanking)}
									disabled={readOnly || franchises?.length < 2}
									onChange={(value) => handleUpdateSwitches(sectionId, "simultaneousRanking", value)}
									tooltip={{
										title: "Require rankings in all franchises simultaneously, makes all tabs share the same number of rows and columns",
										placement: "bottom",
										key: "ranking-bonus-simultaneous-ranking-tooltip",
									}}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
RankingBonusMakes.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		simultaneousRanking: PropTypes.bool,
	}),
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
